import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useIntl } from 'react-intl';
import { NuDsContext } from '@nubank/nuds-web/components/NuDSProvider/NuDSProvider';
import Typography from '@nubank/nuds-web/components/Typography/Typography';
import Link from '@nubank/nuds-web/components/Link/Link';
import Box from '@nubank/nuds-web/components/Box/Box';

import CpfIcon from './components/CpfIcon';
import PhoneIcon from './components/PhoneIcon';
import EmailIcon from './components/EmailIcon';
import RandomIcon from './components/RandomIcon';
import { KeyOption } from './styles/KeyOption';

const ResponsiveImages = dynamic(import('components/ResponsiveImage/ResponsiveImages'), { ssr: false });

function SectionKeys({ index }) {
  const { formatMessage } = useContext(NuDsContext);
  const intl = useIntl();

  const pixKeys = formatMessage({ id: `cms.pixPage.pixCardsArea[${index}].pixKeys` });
  const pixKeysIconMap = {
    PhoneIcon,
    CpfIcon,
    EmailIcon,
    RandomIcon,
  };

  return (
    <Box
      tag="section"
      paddingHorizontal={{
        xs: '0x',
        md: '8x',
        lg: '12x',
      }}
      paddingVertical={{
        xs: '16x',
        md: '20x',
        lg: '32x',
      }}
    >
      <Box
        display="grid"
        gridColumnGap={{
          xs: '4x',
          md: '8x',
        }}
        gridTemplateColumns={{
          md: 'repeat(6,1fr)',
          lg: 'repeat(12,1fr)',
        }}
        gridTemplateAreas={{
          xs: `
            "image image image"
            "title title title"
            "content content content"
          `,
          md: `
            "title title title title title title"
            "image image image image image image"
            ". . content content content content"
          `,
          lg: `
            ". image image image image . title title title title title ."
            ". image image image image . content content content content content ."
          `,
        }}
      >
        <Box
          gridArea="image"
          alignSelf="center"
          paddingHorizontal={{ md: '4x' }}
          marginVertical={{
            md: '12x',
            lg: '0x',
          }}
        >
          <ResponsiveImages
            className="image"
            fileName="man-holding-device-2/man-holding-device-2.jpg"
            loading="lazy"
            alt={intl.formatMessage({ id: 'PIX.KEYS.IMAGE_ALT' })}
          />
        </Box>

        <Box
          gridArea="title"
          paddingHorizontal={{ xs: '6x' }}
          marginVertical={{
            xs: '6x',
            md: 0,
          }}
        >
          <Typography
            variant="title"
            color="black"
            intlKey={`cms.pixPage.pixCardsArea[${index}].title`}
          />
        </Box>

        <Box
          gridArea="content"
          marginTop={{ lg: '6x' }}
          paddingHorizontal={{ xs: '6x' }}
        >
          <Typography
            color="black"
            variant="subtitle"
            tag="p"
            intlKey={`cms.pixPage.pixCardsArea[${index}].description`}
            gutterBottom={1.5}
          />

          <Typography
            color="black"
            variant="subtitle"
            tag="p"
            intlKey={`cms.pixPage.pixCardsArea[${index}].pixKeysTitle`}
            gutterBottom={1.5}
          />

          <Box
            display="grid"
            gridGap="6x"
            marginBottom="6x"
            gridTemplateColumns={{
              xs: 'none',
              md: 'repeat(2,1fr)',
            }}
          >
            {pixKeys.map(({ title, iconName, id }) => {
              const IconComponent = pixKeysIconMap[iconName];

              if (!IconComponent) return null;

              return (
                <KeyOption key={id}>
                  <IconComponent aria-hidden="true" />
                  <Typography color="black">
                    {title}
                  </Typography>
                </KeyOption>
              );
            })}
          </Box>

          <Typography
            color="black"
            tag="p"
            intlKey={`cms.pixPage.pixCardsArea[${index}].observation`}
            gutterBottom={1.5}
          />

          <Link
            intlKey={`cms.pixPage.pixCardsArea[${index}].ctaText`}
            variant="action"
            href={formatMessage({ id: `cms.pixPage.pixCardsArea[${index}].ctaLink` })}
          />
        </Box>
      </Box>
    </Box>
  );
}

SectionKeys.propTypes = {
  index: PropTypes.number.isRequired,
};

export default React.memo(SectionKeys);
